import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Printer, FileText } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { DocumentRenderer } from "../components/DocumentRenderer";
import { scrollToElement } from "../hooks/useRoute";
import pricingContent from "../../../docs/pricing.md";

const pricingPlans = [
  {
    id: "starter",
    name: "Starter Plan",
    price: "₹7,999",
    category: "static",
    categoryLabel: "Static Website",
    badge: "Micro & Event Sites",
    description: "Single-page scrollable web presence for individuals, events, and micro-businesses.",
    delivery: "5–7 business days",
    revisions: "2 rounds",
    support: "7 calendar days",
    features: [
      "1 single scrollable page (up to 5 sections)",
      "Template design customized with brand colors & logo",
      "Up to 1,500 words text + 10 images placement",
      "Standard contact form with email notifications",
      "Floating WhatsApp click-to-chat button",
      "Basic on-page SEO & Google Search Console setup"
    ],
    anchor: "21-starter-plan-7999",
    popular: false,
  },
  {
    id: "basic",
    name: "Basic Plan",
    price: "₹14,999",
    category: "static",
    categoryLabel: "Static Website",
    badge: "Small Businesses",
    description: "Structured multi-page static website for small businesses, professionals, and service providers.",
    delivery: "7–12 business days",
    revisions: "3 rounds",
    support: "14 calendar days",
    features: [
      "Up to 5 custom pages (Home, About, Services, Gallery, Contact)",
      "Custom responsive design from scratch",
      "Responsive image gallery (up to 30 images)",
      "Contact form with auto-responder email",
      "On-page SEO, XML Sitemap & Google Analytics 4",
      "Free SSL certificate configuration & speed optimization (80+ PageSpeed)"
    ],
    anchor: "22-basic-plan-14999",
    popular: false,
  },
  {
    id: "standard",
    name: "Standard Plan",
    price: "₹22,999",
    category: "static",
    categoryLabel: "Static Website",
    badge: "Comprehensive Sites",
    description: "Comprehensive static site with micro-animations, interactive sections, and rich SEO markup.",
    delivery: "10–15 business days",
    revisions: "3 rounds",
    support: "30 calendar days",
    features: [
      "Up to 8 custom pages with flexible section layouts",
      "Subtle micro-animations, hero sliders & parallax effects",
      "Interactive FAQ accordion, testimonials & team cards",
      "Advanced schema markup (Organization, LocalBusiness, FAQ)",
      "Google Analytics 4 event tracking & Open Graph tags",
      "Speed optimization targeting 90+ desktop / 80+ mobile"
    ],
    anchor: "23-standard-plan-22999",
    popular: false,
  },
  {
    id: "professional",
    name: "Professional Plan",
    price: "₹34,999",
    category: "dynamic",
    categoryLabel: "Dynamic CMS",
    badge: "Most Popular",
    description: "CMS-powered dynamic website with self-managed blog, media gallery, and admin training.",
    delivery: "15–20 business days",
    revisions: "3 rounds",
    support: "30 calendar days",
    features: [
      "Up to 12 pages + Content Management System (CMS)",
      "Self-managed blog / news section with categories & search",
      "User-friendly admin dashboard with 1-on-1 training session",
      "Security hardening (login protection, anti-spam, 2FA)",
      "Automated daily database & file backups",
      "Advanced SEO & schema structured data"
    ],
    anchor: "31-professional-plan-34999-cms-based-dynamic-website",
    popular: true,
  },
  {
    id: "business",
    name: "Business Plan",
    price: "₹64,999+",
    category: "dynamic",
    categoryLabel: "E-Commerce",
    badge: "Online Stores",
    description: "Full e-commerce platform with payment gateway, cart, user accounts, and product catalog.",
    delivery: "20–30 business days",
    revisions: "4 rounds",
    support: "45 calendar days",
    features: [
      "Up to 20 pages + product catalog (50 initial products loaded)",
      "Secure payment gateway integration",
      "Shopping cart, checkout flow & user account management",
      "Product categories, filters, search & inventory tracking",
      "Web Application Firewall & automated daily backups",
      "Global CDN integration & high-speed caching"
    ],
    anchor: "32-business-plan-starting-at-64999-advanced-dynamic-e-commerce",
    popular: false,
  },
  {
    id: "custom-app",
    name: "Web & IoT Application",
    price: "₹99,999+",
    category: "custom",
    categoryLabel: "Custom Engineering",
    badge: "Bespoke Platforms",
    description: "Bespoke web applications, SaaS products, IoT dashboard backends, custom APIs, and scalable cloud architecture.",
    delivery: "Custom timeline",
    revisions: "Iterative sprints",
    support: "60 calendar days",
    features: [
      "Custom UI/UX prototyping & system architecture design",
      "RESTful / WebSocket / MQTT APIs & IoT telemetry connectors",
      "Relational / Time-Series / NoSQL database design & pipelines",
      "CI/CD pipeline setup & managed cloud deployment",
      "OWASP Top 10 security alignment & access logging",
      "60 days of priority technical support & maintenance"
    ],
    anchor: "5-web-application-development-starting-at-99999",
    popular: false,
  },
];

const addOns = [
  { name: "Additional Static Page", price: "₹1,499 / page", desc: "Design & content integration for extra pages beyond plan limit." },
  { name: "Additional Product Entry", price: "₹99 / product", desc: "Product data entry, images, and configuration for e-commerce stores." },
  { name: "Website Migration", price: "₹3,999", desc: "Complete migration of files, database, and DNS setup to new host." },
  { name: "Security Hardening", price: "₹3,999", desc: "Security audit, firewall configuration, login protection & malware scan." },
  { name: "Speed & Performance", price: "₹4,999", desc: "Advanced caching, image compression, database optimization & CDN." },
  { name: "Logo & Brand Identity", price: "₹4,999", desc: "Custom logo design, typography palette & brand identity guidelines." },
  { name: "Content Writing", price: "₹1.50 / word", desc: "SEO-optimized professional copywriting for website pages." },
  { name: "Cookie Consent Banner", price: "₹999", desc: "DPDP / GDPR compliant banner with preference management." },
];

export function PricingPage({ scrollTo }: { scrollTo?: string }) {
  const [activeTab, setActiveTab] = useState<"all" | "static" | "dynamic" | "custom">("all");
  const [showFullDoc, setShowFullDoc] = useState(false);

  useEffect(() => {
    if (scrollTo) {
      const matchedPlan = pricingPlans.find((p) => p.id === scrollTo);
      if (matchedPlan) {
        setActiveTab("all");
      }
      scrollToElement(scrollTo);
    }
  }, [scrollTo]);

  const filteredPlans = activeTab === "all" 
    ? pricingPlans 
    : pricingPlans.filter((p) => p.category === activeTab);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Interactive Screen View */}
      <div className="no-print bg-slate-50 dark:bg-[#070b12] min-h-screen transition-colors">
        {/* Hero Section */}
        <PageHero
          badge="Transparent Engagement Models"
          title="Service Pricing Schedule"
          subtitle="Clear, Structured & Predictable Pricing"
          description="Explore our structured packages for static websites, dynamic CMS applications, e-commerce stores, and custom software engineering. All prices in INR (₹)."
          maxWidth="max-w-5xl"
        />

        {/* Category Filter Tabs */}
        <section className="py-12 bg-white dark:bg-[#0b131e] border-b border-slate-200 dark:border-slate-800 transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {[
                { id: "all", label: "All Plans" },
                { id: "static", label: "Static Websites" },
                { id: "dynamic", label: "Dynamic & E-Commerce" },
                { id: "custom", label: "Custom Applications" },
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`relative px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer ${
                      isActive
                        ? "text-white dark:text-slate-950 font-bold"
                        : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activePricingTabPill"
                        className="absolute inset-0 bg-brand dark:bg-primary rounded-full shadow-xs"
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Plans Grid */}
        <section className="py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <h2 className="sr-only">Available Service Packages</h2>
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
              <AnimatePresence mode="popLayout">
                {filteredPlans.map((plan) => (
                  <motion.div
                    layout
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ y: -6, transition: { duration: 0.2 } }}
                    key={plan.id}
                    id={plan.id}
                    className={`card-interactive scroll-mt-28 relative flex flex-col justify-between rounded-2xl p-8 transition-colors ${
                      plan.popular
                        ? "bg-gradient-to-b from-slate-900 via-brand-dark to-slate-950 text-white shadow-xl ring-2 ring-primary"
                        : "bg-white dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 shadow-xs hover:border-slate-300 dark:hover:border-slate-700"
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="bg-primary text-slate-950 text-xs font-extrabold uppercase tracking-wider px-4 py-1 rounded-full shadow-sm">
                          {plan.badge}
                        </span>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <span
                          className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${
                            plan.popular
                              ? "bg-white/10 text-brand-light"
                              : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                          }`}
                        >
                          {plan.categoryLabel}
                        </span>
                        {!plan.popular && (
                          <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                            {plan.badge}
                          </span>
                        )}
                      </div>

                      <h3 className={`text-2xl font-bold ${plan.popular ? "text-white" : "text-brand-dark dark:text-slate-100"}`}>
                        {plan.name}
                      </h3>

                      <div className="mt-3 flex items-baseline gap-1">
                        <span className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${plan.popular ? "text-white" : "text-brand-dark dark:text-slate-100"}`}>
                          {plan.price}
                        </span>
                        <span className={`text-xs font-medium ${plan.popular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}>
                          + GST
                        </span>
                      </div>

                      <p className={`mt-3 text-sm leading-relaxed ${plan.popular ? "text-slate-300" : "text-slate-600 dark:text-slate-300"}`}>
                        {plan.description}
                      </p>

                      <div className="mt-6 pt-5 border-t border-slate-200/20 dark:border-slate-800 space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className={plan.popular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}>Delivery Time:</span>
                          <span className="font-semibold">{plan.delivery}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={plan.popular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}>Revision Rounds:</span>
                          <span className="font-semibold">{plan.revisions}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={plan.popular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}>Complimentary AMC:</span>
                          <span className="font-semibold">{plan.support}</span>
                        </div>
                      </div>

                      <div className="mt-6 pt-5 border-t border-slate-200/20 dark:border-slate-800 space-y-2.5">
                        <p className={`text-xs font-bold uppercase tracking-wider ${plan.popular ? "text-primary" : "text-brand dark:text-primary"}`}>
                          What's Included:
                        </p>
                        <ul className="space-y-2">
                          {plan.features.map((feat) => (
                            <li key={feat} className={`flex items-start gap-2 text-xs leading-relaxed ${plan.popular ? "text-slate-300" : "text-slate-700 dark:text-slate-300"}`}>
                              <svg className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${plan.popular ? "text-primary" : "text-brand dark:text-primary"}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              <span>{feat}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-8 pt-4 border-t border-slate-200/20 dark:border-slate-800">
                      <a
                        href="/contact"
                        className={`w-full text-center py-3 px-4 rounded-lg font-bold text-xs uppercase tracking-wider min-h-[44px] flex items-center justify-center transition-all ${
                          plan.popular
                            ? "bg-primary hover:bg-sky-400 text-slate-950 shadow-md"
                            : "bg-brand dark:bg-primary text-white dark:text-slate-950 hover:bg-brand-dark dark:hover:bg-sky-400 shadow-xs"
                        }`}
                      >
                        Select Plan & Inquire
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>

        {/* Add-On Services Grid */}
        <section className="py-16 sm:py-20 bg-white dark:bg-[#0b131e] border-t border-slate-200 dark:border-slate-800 transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Flexible Add-Ons</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
                Enhance your project with targeted add-on modules.
              </h2>
              <p className="mt-3 text-slate-600 dark:text-slate-300">
                Tailor any plan with additional pages, migrations, security audits, and speed optimizations.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {addOns.map((item) => (
                <div key={item.name} className="bg-slate-50 dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col justify-between transition-colors">
                  <div>
                    <h3 className="text-base font-bold text-brand-dark dark:text-slate-100 mb-1">{item.name}</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-4">{item.desc}</p>
                  </div>
                  <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <span className="text-xs text-slate-400 uppercase font-semibold">Standard Fee</span>
                    <span className="text-sm font-extrabold text-brand dark:text-primary">{item.price}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom Full Statutory Pricing Document Action Card */}
        <section className="py-12 bg-slate-100 dark:bg-[#070b12] border-t border-slate-200 dark:border-slate-800 transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white dark:bg-[#0e1724] rounded-2xl border border-slate-200 dark:border-slate-800 p-8 shadow-sm max-w-3xl mx-auto space-y-4 transition-colors">
              <h3 className="text-xl font-bold text-brand-dark dark:text-slate-100">
                Need Complete Line-Item & Statutory Specifications?
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                Access our full official pricing schedule including detailed exclusions, payment milestones, support terms, and statutory details.
              </p>
              <div className="flex flex-wrap justify-center gap-3 pt-2">
                <button
                  onClick={() => setShowFullDoc(!showFullDoc)}
                  className="inline-flex items-center gap-2 bg-brand dark:bg-primary text-white dark:text-slate-950 font-semibold text-sm px-6 py-3 rounded-lg hover:bg-primary dark:hover:bg-sky-400 transition-all shadow-sm cursor-pointer"
                >
                  <FileText className="w-4 h-4 shrink-0" />
                  <span>{showFullDoc ? "Hide Full Pricing Document" : "Get Full Pricing Document"}</span>
                </button>

                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-brand dark:text-slate-100 font-semibold text-sm px-5 py-3 rounded-lg border border-slate-200 dark:border-slate-700 transition-all cursor-pointer shadow-xs"
                >
                  <Printer className="w-4 h-4 text-brand dark:text-primary shrink-0" />
                  <span>Print Document</span>
                </button>
              </div>
            </div>

            {showFullDoc && (
              <div className="mt-8 bg-white dark:bg-[#0e1724] text-left rounded-2xl border border-slate-200 dark:border-slate-800 p-6 sm:p-10 shadow-sm doc-section transition-colors">
                <DocumentRenderer content={pricingContent} />
              </div>
            )}
          </div>
        </section>

        {/* Contact Navigation CTA */}
        <section className="no-print bg-slate-900 py-16 sm:py-20 text-white rounded-2xl border border-slate-800 mt-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4">
              Need a Custom Project Quote or Scope SLA?
            </h2>
            <p className="text-slate-400 text-base sm:text-lg mb-8 leading-relaxed">
              Have specific feature requirements, enterprise AMC terms, or institutional budget constraints? Talk directly with our pricing & estimation engineers.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-8 py-3.5 rounded-xl font-bold text-base transition-all duration-200 shadow-lg"
            >
              Request Custom Proposal on Contact Page →
            </a>
          </div>
        </section>
      </div>

      {/* Print-Only Detailed Schedule Document (Active when printing) */}
      <div className="print-only">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="doc-section">
            <DocumentRenderer content={pricingContent} />
          </div>
        </div>
      </div>
    </>
  );
}
